import React, { Component } from 'react'
import ReactToPrint, { PrintContextConsumer } from 'react-to-print'
import api from '../../api/api.js'

import { withStyles } from '@mui/styles'
import {
  Alert,
  AppBar,
  CircularProgress,
  Dialog,
  IconButton,
  Snackbar,
  Toolbar,
  Tooltip,
  Typography,
} from '@mui/material'

import CloseIcon from '@mui/icons-material/Close'
import EmailIcon from '@mui/icons-material/Email'

import { styles } from './authStyles.js'
import AssessmentEditor from '../visit/AssessmentEditor.jsx'
import AuthorizePages from './AuthorizePages.jsx'
import InvoicePages from './InvoicePages.jsx'
import {
  buildAuthorizationAssessmentRows,
  buildAuthorizationEmailHtml as buildAuthorizationEmailHtmlTemplate,
  buildInvoiceEmailHtml as buildInvoiceEmailHtmlTemplate,
} from './emailTemplates.js'
import logo from '../img/logo.png'

const DEMO_MAIL_CONFIG_SET = 'runtimeconfigYahoo.json'

const TREATCODE88100 = '88100 - R266.70 - home visit'
const TREATCODE88002 = '88002 - R540.50 - consultation, wound assessment, and counseling'
const TREATCODE88041 = '88041 - R205.60 - treatment of extensive wounds, charged on every visit'
const ICD10CODES = 'Z48.0, T81.4, Y83.8'
const TARIFFCODE = '88301'

const assessment0 = {
  // Legacy fields
  allergies: '',
  balance: 'unknown',
  comorbidities: '',
  depth: 1,
  education: '',
  exudateLevel: '1',
  exudateType: '',
  healingFactors: '',
  healthEducation: '',
  infection: false,
  inflammation: false,
  length: 1,
  margin: 'unknown',
  medInfo: '',
  medications: '',
  mobility: '',
  nutrition: '',
  painLevel: 0,
  postsurgical: false,
  site: '',
  smoking: '',
  treatment: '',
  treatmentPlan: '',
  type: '',
  viability: 'unknown',
  patientId: '',
  visitId: '',
  weight: 0,
  width: 1,
  // New assessment overlay fields
  woundStartDate: '',
  treatmentStartDate: '',
  woundLocation: '',
  diabeticUlcer: false,
  pressureUlcer: false,
  venousUlcer: false,
  trauma: false,
  burns: false,
  other: false,
  surgicalProc: '',
  dstage: '',
  pstage: '',
  vstage: '',
  traumaDesc: '',
  burnDegree1: false,
  burnDegree2: false,
  burnDegree3: false,
  burnArea: '',
  cellulitis: false,
  colonised: false,
  superficial: false,
  nerds: {},
  stones: {},
  serous: '',
  sanguineous: '',
  serosang: '',
  purulent: '',
  otherDesc: '',
  // Discovery wound assessment fields
  outpatientPracticeNo: '',
  bodyAnnotation: '',
  fistulae: false,
  pilonidalCyst: false,
  skinCancer: false,
  arterialUlcer: false,
  gangren: false,
  skinGrafting: false,
  healableWound: false,
  maintenanceWound: false,
  nonHealableWound: false,
  healabilityOther: '',
  siteOfWound: '',
  sizeOfWound: '',
  woundLength: '',
  woundWidth: '',
  woundDepth: '',
  woundVolume: '',
  woundUndermining: '',
  epithelialisation: false,
  granulation: false,
  slough: false,
  blackNecrotic: false,
  overGranulating: false,
  fungating: false,
  mixedTissue: false,
  biofilm: false,
  exudateColour: '',
  exudateAmount: '',
  skinHealthy: false,
  skinDry: false,
  skinDiscoloured: false,
  skinErythema: false,
  skinMacerated: false,
  skinCallous: false,
  skinOedematous: false,
  woundPhaseInflammatory: false,
  woundPhaseProliferation: false,
  woundPhaseMaturation: false,
  painFrequency: '',
  treatmentGoals: {},
  treatmentDates: '',
  includePhoto: false,
  clinicalMotivation: false,
  quotation: false,
  assessmentSignatureDataUrl: '',
  assessmentSignatureDate: '',
}

const prodListColumns = [
  { field: 'tariffCode', headerName: 'Tariff Code', width: 100, align: 'left', editable: false },
  { field: 'nappiCode', headerName: 'Nappi Code', width: 115, align: 'left', editable: false },
  { field: 'title', headerName: 'Materials', width: 450, align: 'left', editable: false },
  { field: 'quantity', headerName: 'Qty', width: 100, align: 'right', editable: true },
  { field: 'amount', headerName: 'Cost, excl. VAT', width: 124, align: 'right', editable: true },
]

const prodListColumnsInv = [
  { field: 'date', headerName: 'Date of Service', width: 110, editable: true },
  { field: 'tariffCode', headerName: 'Tariff Code', width: 86, editable: false },
  { field: 'nappiCode', headerName: 'Nappi Code', width: 106, editable: true },
  { field: 'title', fontSize: 16, headerName: 'Materials', width: 370, editable: true },
  { field: 'ICD10Codes', headerName: 'ICD10 Codes', width: 154, default: '', editable: true },
  { field: 'quantity', headerName: 'Qty', width: 60, editable: true },
  { field: 'amount', headerName: 'Cost (vat incl.)', width: 100, editable: true },
]


function formatNumber(number) {
  number = number.toFixed(2) + ''
  let x = number.split('.')
  let x1 = x[0]
  let x2 = x.length > 1 ? '.' + x[1] : ''
  let rgx = /(\d+)(\d{3})/
  while (rgx.test(x1)) {
    x1 = x1.replace(rgx, '$1,$2')
  }
  return x1 + x2
}

class AuthorizationDialog extends Component {
  constructor(props) {
    super(props)

    this.state = {
      assessment: assessment0,
      dateString: '',
      emailSending: false,
      fullName: '',
      groupDetails: {},
      images: [],
      medicalAidEmail: '',
      medicalAidRequiresDiscoveryPdf: false,
      medicalAids: [],
      paLoading: false,
      patient: {},
      phLoading: false,
      productsCache: [],
      prodList: [],
      sbLoading: false,
      snackbarMessage: '',
      snackbarOpen: false,
      snackbarSeverity: 'info',
      supplRows1: [],
      totals: 0,
    }

    this.componentRef = null
    this.discoveryPdfRef = null
    this.logoInlineAttachment = null
  }

  escapeHtml = (value) => String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')

  formatEmailText = (value) => {
    const safeValue = this.escapeHtml(value)
    return safeValue ? safeValue.replace(/\r?\n/g, '<br />') : '&nbsp;'
  }

  getLogoInlineAttachment = async () => {
    if (this.logoInlineAttachment) {
      return this.logoInlineAttachment
    }

    try {
      const response = await fetch(logo)
      if (!response.ok) {
        return null
      }

      const blob = await response.blob()
      const dataUri = await new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onloadend = () => resolve(reader.result)
        reader.onerror = () => reject(new Error('Failed to read logo data'))
        reader.readAsDataURL(blob)
      })

      if (typeof dataUri !== 'string') {
        return null
      }

      const matches = dataUri.match(/^data:(.+);base64,(.+)$/)
      if (!matches) {
        return null
      }

      this.logoInlineAttachment = {
        cid: 'invoice-logo',
        contentBase64: matches[2],
        contentType: matches[1],
        filename: 'logo.png',
      }
      return this.logoInlineAttachment
    } catch (e) {
      return null
    }
  }

  buildInvoiceEmailHtml = async () => {
    const { dateString, groupDetails, patient, prodList, totals } = this.state
    const logoInlineAttachment = await this.getLogoInlineAttachment()
    const formatter = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'ZAR',
    })

    const rowsHtml = (prodList || []).map((item) => `
      <tr>
        <td style="border:1px solid #d9d9d9;padding:8px;white-space:nowrap;">${this.escapeHtml(item.date)}</td>
        <td style="border:1px solid #d9d9d9;padding:8px;white-space:nowrap;">${this.escapeHtml(item.tariffCode)}</td>
        <td style="border:1px solid #d9d9d9;padding:8px;white-space:nowrap;">${this.escapeHtml(item.nappiCode)}</td>
        <td style="border:1px solid #d9d9d9;padding:8px;">${this.escapeHtml(item.title)}</td>
        <td style="border:1px solid #d9d9d9;padding:8px;white-space:nowrap;">${this.escapeHtml(item.ICD10Codes)}</td>
        <td style="border:1px solid #d9d9d9;padding:8px;text-align:right;white-space:nowrap;">${this.escapeHtml(item.quantity)}</td>
        <td style="border:1px solid #d9d9d9;padding:8px;text-align:right;white-space:nowrap;">${this.escapeHtml(item.amount)}</td>
      </tr>
    `).join('')

    const safeRowsHtml = rowsHtml || `
      <tr>
        <td colspan="7" style="border:1px solid #d9d9d9;padding:10px;text-align:center;color:#666;">No invoice line items</td>
      </tr>
    `

    return `
      <html>
        <head>
          <meta charset="utf-8" />
          <meta name="viewport" content="width=device-width, initial-scale=1" />
        </head>
        <body style="margin:0;padding:16px;background:#f3f5f8;font-family:Arial,sans-serif;color:#1f2937;">
          <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" style="max-width:980px;margin:0 auto;background:#ffffff;border:1px solid #d9d9d9;">
            <tr>
              <td style="padding:16px 24px 0 24px;font-size:14px;line-height:1.6;color:#374151;">
                Hello, please find the invoice for ${this.escapeHtml(patient.firstName)} ${this.escapeHtml(patient.patientName)}. Kind regards.
              </td>
            </tr>
            <tr>
              <td style="padding:20px 24px;border-bottom:1px solid #d9d9d9;">
                <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%">
                  <tr>
                    <td style="vertical-align:top;">
                      <div style="font-size:20px;font-weight:700;line-height:1.4;">${this.escapeHtml(groupDetails.hdetail1)}</div>
                      <div style="font-size:16px;line-height:1.4;">${this.escapeHtml(groupDetails.hdetail2)}</div>
                      <div style="font-size:14px;line-height:1.4;">${this.escapeHtml(groupDetails.hdetail3)}</div>
                      <div style="font-size:13px;line-height:1.6;margin-top:10px;">${this.escapeHtml(groupDetails.hdetail4)}</div>
                      <div style="font-size:13px;line-height:1.6;"><strong>${this.escapeHtml(groupDetails.hdetail5)}</strong> ${this.escapeHtml(groupDetails.hdetail6)}</div>
                      <div style="font-size:13px;line-height:1.6;"><strong>${this.escapeHtml(groupDetails.hdetail7)}</strong> ${this.escapeHtml(groupDetails.hdetail8)}</div>
                      <div style="font-size:13px;line-height:1.6;"><strong>${this.escapeHtml(groupDetails.hdetail9)}</strong> ${this.escapeHtml(groupDetails.hdetail10)}</div>
                    </td>
                    <td style="text-align:right;vertical-align:top;">
                      ${logoInlineAttachment ? '<img src="cid:invoice-logo" alt="Logo" style="max-width:140px;height:auto;border:0;" />' : ''}
                    </td>
                  </tr>
                </table>
              </td>
            </tr>
            <tr>
              <td style="padding:18px 24px 8px 24px;">
                <div style="font-size:20px;font-weight:700;margin-bottom:8px;">Tax Invoice</div>
                <div style="font-size:14px;line-height:1.7;">
                  <div><strong>Invoice Date:</strong> ${this.escapeHtml(dateString)}</div>
                  <div><strong>Patient:</strong> ${this.escapeHtml(patient.firstName)} ${this.escapeHtml(patient.patientName)}</div>
                  <div><strong>Medical Aid:</strong> ${this.escapeHtml(patient.medicalAid)}</div>
                  <div><strong>Member No:</strong> ${this.escapeHtml(patient.memberNo)}</div>
                  <div><strong>Authorization No:</strong> ${this.escapeHtml(patient.authNo)}</div>
                </div>
              </td>
            </tr>
            <tr>
              <td style="padding:0 24px 18px 24px;">
                <div style="font-size:13px;line-height:1.7;color:#374151;border-top:1px solid #e5e7eb;padding-top:12px;">
                  <div><strong>Patient:</strong> ${this.escapeHtml(patient.firstName)} ${this.escapeHtml(patient.patientName)}, d.o.b: ${this.escapeHtml(patient.dateOfBirth)}, Dep Code: ${this.escapeHtml(patient.dependentCode)}</div>
                  <div><strong>Treating Provider:</strong> ${this.escapeHtml(groupDetails.hdetail1)}, ${this.escapeHtml(groupDetails.hdetail11)}, Date of Service: ${this.escapeHtml(dateString)}</div>
                  <div><strong>ICD10 Codes:</strong> ${this.escapeHtml(patient.ICD10Codes)}</div>
                </div>
              </td>
            </tr>
            <tr>
              <td style="padding:8px 24px 24px 24px;">
                <table role="table" cellpadding="0" cellspacing="0" border="0" width="100%" style="border-collapse:collapse;font-size:13px;">
                  <thead>
                    <tr style="background:#2b4287;color:#ffffff;text-align:left;">
                      <th style="padding:10px;border:1px solid #1f3268;white-space:nowrap;">Date of Service</th>
                      <th style="padding:10px;border:1px solid #1f3268;white-space:nowrap;">Tariff Code</th>
                      <th style="padding:10px;border:1px solid #1f3268;white-space:nowrap;">Nappi Code</th>
                      <th style="padding:10px;border:1px solid #1f3268;">Materials</th>
                      <th style="padding:10px;border:1px solid #1f3268;white-space:nowrap;">ICD10 Codes</th>
                      <th style="padding:10px;border:1px solid #1f3268;white-space:nowrap;text-align:right;">Qty</th>
                      <th style="padding:10px;border:1px solid #1f3268;white-space:nowrap;text-align:right;">Cost (vat incl.)</th>
                    </tr>
                  </thead>
                  <tbody>
                    ${safeRowsHtml}
                  </tbody>
                </table>

                <div style="text-align:right;margin-top:12px;font-size:16px;">
                  <strong>Total:</strong> ${formatter.format(Number(totals || 0))}
                </div>
              </td>
            </tr>
            <tr>
              <td style="padding:16px 24px 24px 24px;border-top:1px solid #d9d9d9;font-size:12px;line-height:1.6;color:#374151;">
                <div><strong>Bank Details:</strong> ${this.escapeHtml(groupDetails.bdetail1)}, ${this.escapeHtml(groupDetails.bdetail2)}, Branch ${this.escapeHtml(groupDetails.bdetail5)}, Account no: ${this.escapeHtml(groupDetails.bdetail3)}, Account Type: ${this.escapeHtml(groupDetails.bdetail4)}</div>
                <div><strong>Company Info:</strong> ${this.escapeHtml(groupDetails.cdetail1)}</div>
                <div><strong>EFT Info:</strong> ${this.escapeHtml(groupDetails.edetail1)} ${this.escapeHtml(patient.firstName)} ${this.escapeHtml(patient.patientName)} ${this.escapeHtml(groupDetails.edetail2)} ${this.escapeHtml(groupDetails.edetail3)}</div>
              </td>
            </tr>
          </table>
        </body>
      </html>
    `
  }

  buildAuthorizationEmailHtml = async () => {
    const {
      dateString,
      fullName,
      groupDetails,
      images,
      patient,
      prodList,
      supplRows1,
    } = this.state
    const logoInlineAttachment = await this.getLogoInlineAttachment()

    const supplRows2 = this.getAuthorizationSupplementRows2()

    const keyCellStyle = 'width:160px;text-align:left;font-weight:700;white-space:nowrap;border:1px solid #9aa3af;padding:8px 10px;vertical-align:top;background:#e6e8ed;'
    const valueCellStyle = 'text-align:left;border:1px solid #9aa3af;padding:8px 10px;vertical-align:top;background:#ffffff;line-height:1.5;'
    const prodHeaderStyleBase = 'border:1px solid #7f8897;padding:10px 8px;background:#c7ccd6;color:#1f2937;font-size:13px;font-weight:700;vertical-align:top;'
    const prodCellStyleBase = 'border:1px solid #9aa3af;padding:8px 10px;background:#ffffff;font-size:13px;vertical-align:top;'

    const supplRows1Html = (supplRows1 || []).map((row) => `
      <tr>
        <td style="${keyCellStyle}">${this.formatEmailText(row.key)}</td>
        <td style="${valueCellStyle}">${this.formatEmailText(row.value)}</td>
      </tr>
    `).join('')

    const prodHeaderHtml = prodListColumns.map((column) => `
      <th style="${prodHeaderStyleBase}width:${column.width || 120}px;text-align:${column.align || 'left'};">
        ${this.escapeHtml(column.headerName)}
      </th>
    `).join('')

    const prodRowsHtml = (prodList || []).map((row) => `
      <tr>
        ${prodListColumns.map((column) => {
          const rawValue = row[column.field] ?? ''
          const whiteSpace = column.field === 'title' ? 'normal' : 'nowrap'
          return `
            <td style="${prodCellStyleBase}width:${column.width || 120}px;text-align:${column.align || 'left'};white-space:${whiteSpace};">
              ${this.formatEmailText(rawValue)}
            </td>
          `
        }).join('')}
      </tr>
    `).join('')

    const safeProdRowsHtml = prodRowsHtml || `
      <tr>
        <td colspan="${prodListColumns.length}" style="${prodCellStyleBase}text-align:center;color:#6b7280;">
          No authorization line items
        </td>
      </tr>
    `

    const supplRows2Html = (supplRows2 || []).map((row) => `
      <tr>
        <td style="${keyCellStyle}">${this.formatEmailText(row.key)}</td>
        <td style="${valueCellStyle}">${this.formatEmailText(row.value)}</td>
      </tr>
    `).join('')

    const imagesHtml = (images || []).length > 0
      ? `
        <tr>
          <td style="padding:0 24px 24px 24px;">
            <div style="font-size:18px;font-weight:700;color:#1f2937;margin:0 0 12px 0;">Wound Images</div>
            ${(images || []).map((image) => `
              <div style="margin:0 0 18px 0;">
                <div style="font-size:13px;font-weight:700;color:#374151;margin:0 0 6px 0;">
                  ${this.escapeHtml(image.title || 'Image')}
                </div>
                <img
                  src="${this.escapeHtml(image.imageUrl || '')}"
                  alt="${this.escapeHtml(image.title || 'Wound image')}"
                  style="display:block;width:100%;max-width:900px;height:auto;border:1px solid #d9d9d9;background:#ffffff;"
                />
              </div>
            `).join('')}
          </td>
        </tr>
      `
      : ''

    return `
      <html>
        <head>
          <meta charset="utf-8" />
          <meta name="viewport" content="width=device-width, initial-scale=1" />
        </head>
        <body style="margin:0;padding:16px;background:#d2d5dc;font-family:Arial,sans-serif;color:#1f2937;">
          <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" style="max-width:980px;margin:0 auto;background:#ffffff;border:1px solid #9aa3af;">
            <tr>
              <td style="padding:18px 24px 0 24px;font-size:14px;line-height:1.6;color:#374151;">
                Hello, please find the authorization request for ${this.escapeHtml(patient.firstName)} ${this.escapeHtml(patient.patientName)}.
              </td>
            </tr>
            <tr>
              <td style="padding:20px 24px;border-bottom:1px solid #d9d9d9;">
                <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%">
                  <tr>
                    <td style="vertical-align:top;">
                      <div style="font-size:24px;font-weight:700;line-height:1.3;">
                        ${this.escapeHtml(groupDetails.hdetail1)} - ${this.escapeHtml(groupDetails.hdetail2)}
                      </div>
                      <div style="font-size:16px;line-height:1.4;">
                        ${this.escapeHtml(groupDetails.hdetail11)} / ${this.escapeHtml(groupDetails.hdetail12)}
                      </div>
                      <div style="font-size:13px;line-height:1.6;margin-top:10px;">
                        ${this.escapeHtml(groupDetails.hdetail5)}
                      </div>
                      <div style="font-size:13px;line-height:1.6;">
                        ${this.escapeHtml(groupDetails.hdetail7)}
                      </div>
                    </td>
                    <td style="text-align:right;vertical-align:top;">
                      ${logoInlineAttachment ? '<img src="cid:invoice-logo" alt="Logo" style="max-width:140px;height:auto;border:0;" />' : ''}
                    </td>
                  </tr>
                </table>
              </td>
            </tr>
            <tr>
              <td style="padding:20px 24px 8px 24px;">
                <div style="font-size:24px;font-weight:700;line-height:1.3;margin-bottom:8px;">Authorization Request for wound care</div>
                <div style="font-size:14px;line-height:1.8;">
                  <div><strong>To:</strong> ${this.escapeHtml(patient.medicalAid)}</div>
                  <div><strong>Attention:</strong> Pre-Authorization</div>
                  <div><strong>Name:</strong> ${this.escapeHtml(fullName)}</div>
                  <div><strong>Contact Number:</strong> ${this.escapeHtml(patient.mobile)}</div>
                  <div><strong>Member Number:</strong> ${this.escapeHtml(patient.memberNo)}</div>
                  <div><strong>ID:</strong> ${this.escapeHtml(patient.SAID)}</div>
                  <div><strong>Referring Doctor:</strong> ${this.escapeHtml(patient.referringDoctor)} ${this.escapeHtml(patient.referringDrNo)}</div>
                  <div><strong>Date:</strong> ${this.escapeHtml(dateString)}</div>
                </div>
              </td>
            </tr>
            <tr>
              <td style="padding:8px 24px 18px 24px;">
                <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" style="border-collapse:collapse;">
                  <tbody>
                    ${supplRows1Html}
                  </tbody>
                </table>
              </td>
            </tr>
            <tr>
              <td style="padding:0 24px 18px 24px;">
                <table role="table" cellpadding="0" cellspacing="0" border="0" width="100%" style="border-collapse:collapse;table-layout:fixed;">
                  <thead>
                    <tr>
                      ${prodHeaderHtml}
                    </tr>
                  </thead>
                  <tbody>
                    ${safeProdRowsHtml}
                  </tbody>
                </table>
              </td>
            </tr>
            <tr>
              <td style="padding:0 24px 24px 24px;">
                <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" style="border-collapse:collapse;">
                  <tbody>
                    ${supplRows2Html}
                  </tbody>
                </table>
              </td>
            </tr>
            ${imagesHtml}
          </table>
        </body>
      </html>
    `
  }

  componentDidUpdate(prevProps) {
    const prevVisitId = prevProps.visit?.id
    const nextVisitId = this.props.visit?.id
    if (this.props.open && (!prevProps.open || prevVisitId !== nextVisitId)) {
      this.loadAuthorizationData()
    }
  }

  createData = (key, value) => ({ key, value })

  fetchMedicalAids = async () => {
    try {
      const response = await api.get('/medicalAids/')
      const medicalAids = response.data || []
      this.setState({ medicalAids })
      return medicalAids
    } catch (error) {
      if (error.response?.status === 403) {
        this.props.onSessionExpired?.()
      }
      return []
    }
  }

  getGroup = async () => {
    try {
      this.setState({ paLoading: true })
      const response = await api.get(`/group/${this.props.groupId}`)
      this.setState({ groupDetails: response.data, paLoading: false })
    } catch (error) {
      if (error.response?.status === 403) {
        this.props.onSessionExpired?.()
      }
      this.setState({ paLoading: false })
    }
  }

  fetchProducts = async () => {
    try {
      const response = await api.get(`/products/group/${this.props.groupId}`)
      const productsCache = response.data || []
      this.setState({ productsCache })
      return productsCache
    } catch (error) {
      if (error.response?.status === 403) {
        this.props.onSessionExpired?.()
      }
      return []
    }
  }

  getAssessment = async (patientId) => {
    if (!patientId) {
      this.setState({ assessment: assessment0 })
      return
    }

    try {
      this.setState({ paLoading: true })
      const response = await api.get(`/assessment/patient/${patientId}`)
      let assessment = assessment0
      if (response.data.length > 0) {
        assessment = { ...assessment0, ...response.data[0] }
      }
      this.setState({ assessment, paLoading: false })
    } catch (error) {
      if (error.response?.status === 403) {
        this.props.onSessionExpired?.()
      }
      this.setState({ paLoading: false })
    }
  }

  fetchImages = async (visitId) => {
    try {
      this.setState({ phLoading: true, images: [] })
      const response = await api.get(`/photoBatch/${visitId}`)
      this.setState({ images: response.data || [], phLoading: false })
    } catch (error) {
      if (error.response?.status === 403) {
        this.props.onSessionExpired?.()
      }
      this.setState({ phLoading: false })
    }
  }

  getStockItems = async (visitId, visitDate, ICD10Codes) => {
    try {
      this.setState({ prodList: [], sbLoading: true })
      const response = await api.get(`/stockBatch/${visitId}`)
      let availableProducts = this.props.products || []
      if (availableProducts.length === 0) {
        availableProducts = this.state.productsCache.length > 0
          ? this.state.productsCache
          : await this.fetchProducts()
      }

      let totals = 0
      const prodList = []

      for (const rdata of response.data || []) {
        const quantity = rdata.quantity === undefined ? 1 : rdata.quantity
        const targetName = String(rdata.product || '').trim().toLowerCase()
        const product = availableProducts.find(
          (p) => String(p.title || '').trim().toLowerCase() === targetName,
        )
        if (!product) {
          continue
        }

        let sp = Number(`${product.sellingPrice}`) * quantity
        let tariffCode = TARIFFCODE
        let nappiCode = product.nappiCode

        if ((nappiCode || '').substr(0, 2) === '88') {
          tariffCode = nappiCode
          nappiCode = ''
        }

        prodList.push({
          id: Math.random().toString(36).substr(2, 16),
          visitId,
          date: visitDate,
          tariffCode,
          nappiCode: `${nappiCode || ''}`,
          title: `${product.title}`,
          ICD10Codes,
          quantity,
          amount: formatNumber(sp),
          amt: sp,
        })

        totals += sp
      }

      this.setState({ prodList, totals, sbLoading: false })
    } catch (error) {
      if (error.response?.status === 403) {
        this.props.onSessionExpired?.()
      }
      this.setState({ sbLoading: false })
    }
  }

  getPatient = async (visit, medicalAids) => {
    const SAID = visit?.SAID
    if (!SAID) {
      return
    }

    const dateString = visit.date || ''
    this.setState({ dateString })

    try {
      this.setState({ paLoading: true })
      const response = await api.get(`/patients/${SAID}`)
      const patientData = response.data?.[0]
      if (!patientData) {
        this.setState({ paLoading: false })
        return
      }

      const fullName = `${patientData.gender || ''} ${patientData.firstName || ''} ${patientData.patientName || ''}`.trim()

      let codes = ICD10CODES
      if (!(patientData.ICD10Codes === '' || patientData.ICD10Codes === undefined)) {
        codes = patientData.ICD10Codes
      }

      const supplRows1 = (visit.home === '1')
        ? [
            // this.createData('Treatment Dates:', dateString),
            this.createData('Treatment Dates: ', 'As per attached wound assessment form'),
            this.createData('Treatment Codes:', TREATCODE88100),
            this.createData(' ', TREATCODE88002),
            this.createData(' ', TREATCODE88041),
            this.createData('ICD10 Codes:', codes),
          ]
        : [
            // this.createData('Treatment Dates:', dateString),
            this.createData('Treatment Dates: ', 'As per attached wound assessment form'),
            this.createData('Treatment Codes:', TREATCODE88002),
            this.createData(' ', TREATCODE88041),
            this.createData('ICD10 Codes:', codes),
          ]

      const patient = { ...patientData, ICD10Codes: codes }
      const medicalAidRecord = (medicalAids || []).find((m) => m.name === patient.medicalAid)
      const medicalAidEmail = medicalAidRecord ? medicalAidRecord.email : ''
      const medicalAidRequiresDiscoveryPdf = !!medicalAidRecord?.requiresDiscoveryPdf

      this.setState({
        patient,
        medicalAidEmail,
        medicalAidRequiresDiscoveryPdf,
        paLoading: false,
        fullName,
        supplRows1,
      })

      await this.getStockItems(visit.id, visit.date, codes)
      return patient
    } catch (error) {
      if (error.response?.status === 403) {
        this.props.onSessionExpired?.()
      }
      this.setState({ paLoading: false })
      return null
    }
  }

  loadAuthorizationData = async () => {
    const { visit, mode } = this.props
    if (!visit?.id) {
      return
    }

    const medicalAids = this.state.medicalAids.length > 0
    ? this.state.medicalAids
    : await this.fetchMedicalAids()
    this.getGroup()
    const patient = await this.getPatient(visit, medicalAids)

    if (mode !== 'invoice') {
      this.getAssessment(patient?.id)
      this.fetchImages(visit.id)
    } else {
      this.setState({
        assessment: assessment0,
        images: [],
        phLoading: false,
      })
    }
  }

  handleRemoveImage = (imageId) => {
    const images = this.state.images.filter((m) => m.id !== imageId)
    this.setState({ images })
  }

  handleClose = () => {
    this.setState({ images: [], prodList: [] })
    this.props.onClose?.()
  }

  getAuthorizationSupplementRows2 = () => {
    if (this.state.medicalAidRequiresDiscoveryPdf) {
      return []
    }

    return buildAuthorizationAssessmentRows(this.state.assessment, {
      omitWoundSpecific: false,
    }).map((row) => this.createData(row.key, row.value))
  }

  getDiscoveryPdfAttachment = async () => {
    if (!this.discoveryPdfRef) {
      return null
    }

    const { generatePdfAttachmentFromElement } = await import('../../util/generatePdfAttachment')
    const patientName = `${this.state.patient?.firstName || ''} ${this.state.patient?.patientName || ''}`
      .trim()
      .replace(/[^a-z0-9._-]+/gi, '_') || 'patient'

    return generatePdfAttachmentFromElement(
      this.discoveryPdfRef,
      `${patientName}_wound_assessment.pdf`,
    )
  }

  getArchiveImagesForSend = async (isInvoice) => {
    if (isInvoice) {
      return []
    }

    let archiveImages = (this.state.images || [])
      .map((image) => ({
        imageUrl: image?.imageUrl,
        title: image?.title,
      }))
      .filter((image) => String(image.imageUrl || '').trim() !== '')

    if (archiveImages.length > 0) {
      return archiveImages
    }

    const visitId = this.props.visit?.id
    if (!visitId) {
      return []
    }

    try {
      const response = await api.get(`/photoBatch/${visitId}`)
      const fetchedImages = response.data || []
      this.setState({ images: fetchedImages })
      archiveImages = fetchedImages
        .map((image) => ({
          imageUrl: image?.imageUrl,
          title: image?.title,
        }))
        .filter((image) => String(image.imageUrl || '').trim() !== '')
      return archiveImages
    } catch (error) {
      console.log('Failed to fetch archive images at send time:', error)
      return []
    }
  }

  getAuthorizationEditedRowsFromPrintView = () => {
    if (!this.componentRef) {
      return null
    }

    const normalize = (value) => String(value || '').replace(/\u00a0/g, ' ').trim()

    const readRows = (sectionName) => {
      const rowEls = Array.from(this.componentRef.querySelectorAll(`[data-email-section="${sectionName}"]`))
      return rowEls.map((rowEl, index) => {
        const keyEl = rowEl.querySelector('[data-email-role="key"]')
        const valueEl = rowEl.querySelector('[data-email-role="value"]')
        const key = normalize(keyEl?.textContent)
        const value = normalize(valueEl?.innerText || valueEl?.textContent)
        return {
          key: key || `row-${index + 1}`,
          value,
        }
      })
    }

    return {
      supplRows1: readRows('supplRows1'),
      supplRows2: readRows('supplRows2'),
    }
  }

  showSnackbar = (message, severity = 'info') => {
    this.setState({
      snackbarMessage: String(message || ''),
      snackbarOpen: true,
      snackbarSeverity: severity,
    })
  }

  handleSnackbarClose = (_, reason) => {
    if (reason === 'clickaway') {
      return
    }
    this.setState({ snackbarOpen: false })
  }

  handleSendEmail = async () => {
    if (this.state.emailSending) {
      return
    }

    if (!this.componentRef) {
      this.showSnackbar('Print content is not available yet. Please open the print view and try again.', 'warning')
      return
    }

    const isInvoice = this.props.mode === 'invoice'

    // ── SYNCHRONOUS: capture live DOM values before any async work ──
    const capturedAuthRows = !isInvoice
      ? this.getAuthorizationEditedRowsFromPrintView()
      : null
    // ── end synchronous capture ──

    const modeLabel = isInvoice ? 'Invoice' : 'Authorization'
    const patientName = `${this.state.patient?.firstName || ''} ${this.state.patient?.patientName || ''}`.trim()
    const groupName = this.state.groupDetails?.hdetail1 || 'Wound Care'
//    const subject = `${modeLabel} PDF - ${patientName || 'Patient'} - ${this.state.dateString || ''}`.trim()
    const subject = isInvoice
    ? `Woundcare Invoice for ${patientName || 'Patient'}`.trim()
    : `Woundcare Authorization Request for ${patientName || 'Patient'}, membership no. ${this.state.patient?.memberNo || ''}`.trim()
    const text = isInvoice 
    ?(
      `Hello,\n\n` +
      `please find the ${modeLabel.toLowerCase()} for ${patientName || 'the patient'}.\n` +
      `Regards,\n${groupName}`
    ) : ( 
      `Hello,\n\n` +
      `please find the ${modeLabel.toLowerCase()} information for ${patientName || 'the patient'}.\n` +
      `Clinical images are attached in a single PDF file.\n` +
      `Regards,\n${groupName}`
    )

    const logoInlineAttachment = await this.getLogoInlineAttachment()
    const discoveryPdfAttachment = !isInvoice && this.state.medicalAidRequiresDiscoveryPdf
      ? await this.getDiscoveryPdfAttachment()
      : null

    if (!isInvoice && this.state.medicalAidRequiresDiscoveryPdf && !discoveryPdfAttachment) {
      this.showSnackbar('Unable to generate the Discovery wound assessment PDF. Please try again.', 'error')
      return
    }

    // Build email HTML using clean inline-styled templates,
    // feeding in the DOM-captured edited values.
    let html
    if (isInvoice) {
      html = buildInvoiceEmailHtmlTemplate({
        dateString: this.state.dateString,
        groupDetails: this.state.groupDetails,
        patient: this.state.patient,
        prodList: this.state.prodList,
        totals: this.state.totals,
        logoInlineAttachment,
      })
    } else {
      const supplRows1 = capturedAuthRows?.supplRows1?.length > 0
        ? capturedAuthRows.supplRows1
        : this.state.supplRows1
      const shouldSuppressWoundHistory = Boolean(discoveryPdfAttachment)
      const supplRows2 = shouldSuppressWoundHistory
        ? []
        : (capturedAuthRows?.supplRows2?.length > 0
          ? capturedAuthRows.supplRows2
          : this.getAuthorizationSupplementRows2())
      html = buildAuthorizationEmailHtmlTemplate({
        assessment: this.state.assessment,
        dateString: this.state.dateString,
        fullName: this.state.fullName,
        groupDetails: this.state.groupDetails,
        images: [],
        patient: this.state.patient,
        prodList: this.state.prodList,
        prodListColumns,
        supplRows1,
        supplRows2,
        logoInlineAttachment,
      })
    }

    const inlineAttachments = [logoInlineAttachment].filter(Boolean)
    const archiveImages = await this.getArchiveImagesForSend(isInvoice)
    const archiveName = archiveImages.length > 0
      ? `${(patientName || 'patient').replace(/[^a-z0-9._-]+/gi, '_')}_${(this.state.dateString || 'visit').replace(/[^a-z0-9._-]+/gi, '_')}_clinical_images.pdf`
      : undefined

    const recipientEmail = isInvoice
      ? String(this.state.patient?.email || '').trim()
      : this.state.medicalAidEmail
    if (!recipientEmail) {
      const missingEmailMessage = isInvoice
        ? `No email address found for patient "${patientName || 'unknown'}". Please add the patient's email address and try again.`
        : `No email address found for medical aid "${this.state.patient?.medicalAid || 'unknown'}". Please add the email address in the Medical Aids list and try again.`
      this.showSnackbar(missingEmailMessage, 'warning')
      return
    }

    try {
      this.setState({ emailSending: true })
      await api.post('/email/send', {
        to: recipientEmail,
        subject,
        text,
        html,
        ...(discoveryPdfAttachment || {}),
        inlineAttachments,
        ...(archiveImages.length > 0 ? { archiveImages, archiveName } : {}),
        mailConfigSet: DEMO_MAIL_CONFIG_SET,
      })
      if (!isInvoice) {
        const patientId = this.state.patient?.id
        if (patientId) {
          const { id, ...patientData } = this.state.patient
          const updatedPatientData = {
            ...patientData,
            authStatus: 'pending',
          }
          try {
            await api.put(`/patient/${patientId}`, updatedPatientData)
          } catch (updateError) {
            console.log('Failed to update patient authStatus:', updateError)
          }
        }
      }
      this.props.onEmailSent?.()
      this.showSnackbar(`Email sent successfully to ${recipientEmail}.`, 'success')
    } catch (error) {
      const isPayloadTooLarge = error?.response?.status === 413
      const message = isPayloadTooLarge
        ? 'Email payload is too large for the server/proxy limit. Please use a smaller attachment or increase request size limits.'
        : (error.response?.data?.error || 'Failed to send email.')
      this.showSnackbar(message, 'error')
      console.log(error)
    } finally {
      this.setState({ emailSending: false })
    }
  }

  render() {
    const { classes, open, mode } = this.props
    const isInvoice = mode === 'invoice'
    const { dateString, emailSending, fullName, groupDetails, paLoading, patient, sbLoading } = this.state

    const supplRows2 = this.getAuthorizationSupplementRows2()

    const fileName = `${patient.patientName || 'Patient'}_${patient.firstName || ''}_${dateString}`

    const printButton = (
      <ReactToPrint content={() => this.componentRef} documentTitle={fileName} removeAfterPrint='true'>
        <PrintContextConsumer>
          {({ handlePrint }) => (
            <button className={classes.butto} onClick={handlePrint}>
              Print PDF
            </button>
          )}
        </PrintContextConsumer>
      </ReactToPrint>
    )

    const emailButton = (
      <button
        className={classes.buttoSecondary}
        onClick={this.handleSendEmail}
        disabled={emailSending}
      >
        {emailSending ? (
          <CircularProgress size={16} color="inherit" style={{ marginRight: 8, verticalAlign: 'text-bottom' }} />
        ) : (
          <EmailIcon style={{ fontSize: 18, marginRight: 8, verticalAlign: 'text-bottom' }} />
        )}
        {emailSending ? 'Sending...' : 'Send Email'}
      </button>
    )

    return (
      <Dialog
        fullScreen
        open={open}
        onClose={this.handleClose}
      >
        {!isInvoice && this.state.medicalAidRequiresDiscoveryPdf && (
          <div
            ref={(el) => { this.discoveryPdfRef = el }}
            aria-hidden="true"
            style={{
              position: 'fixed',
              left: '-10000px',
              top: 0,
              width: '1200px',
              background: '#ffffff',
              pointerEvents: 'none',
              overflow: 'visible',
            }}
          >
            <AssessmentEditor
              classes={{}}
              assessment={this.state.assessment}
              patient={this.state.patient}
              groupDetails={this.state.groupDetails}
              onChange={() => {}}
              pdfMode
            />
          </div>
        )}
        <AppBar position="static">
          <Toolbar variant="dense">
            <Tooltip title="Close Modal" placement="top">
              <IconButton
                edge="start"
                color="inherit"
                onClick={this.handleClose}
                aria-label="close"
                size="large"
              >
                <CloseIcon />
              </IconButton>
            </Tooltip>
            <Typography variant="h6" className={classes.poptitle}>
              {'Print View'}
            </Typography>
            {emailButton}
            {printButton}
          </Toolbar>
        </AppBar>

        <div style={{ height: 1500, width: '500%' }} ref={(el) => (this.componentRef = el)}>
          <p />
          {(sbLoading || paLoading) && (
            <CircularProgress size={150} className={classes.uiProgess} />
          )}
          {isInvoice ? (
            <InvoicePages
              classes={classes}
              groupDetails={groupDetails}
              patient={patient}
              dateString={dateString}
              prodList={this.state.prodList}
              prodListColumnsInv={prodListColumnsInv}
              totals={this.state.totals}
            />
          ) : (
            <AuthorizePages
              classes={classes}
              groupDetails={groupDetails}
              patient={patient}
              fullName={fullName}
              supplRows1={this.state.supplRows1}
              prodList={this.state.prodList}
              prodListColumns={prodListColumns}
              supplRows2={supplRows2}
              images={this.state.images}
              onRemoveImage={this.handleRemoveImage}
            />
          )}
        </div>
        <Snackbar
          open={this.state.snackbarOpen}
          autoHideDuration={5000}
          onClose={this.handleSnackbarClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert onClose={this.handleSnackbarClose} severity={this.state.snackbarSeverity} variant="filled">
            {this.state.snackbarMessage}
          </Alert>
        </Snackbar>
      </Dialog>
    )
  }
}

export default withStyles(styles)(AuthorizationDialog)
